import { useState, useEffect, useCallback, useRef } from 'react';
import { getMaintenanceSchedule } from '../services/maintenanceService.js';

export const DEFAULT_MAINTENANCE_MESSAGE =
  'Daily data synchronization and system maintenance is currently in progress. Dashboard operations are temporarily paused to prevent data conflicts. System will resume shortly.';

/**
 * Format seconds into mm:ss or hh:mm:ss.
 */
export function formatCountdownTime(totalSeconds) {
  if (totalSeconds <= 0) return '00:00';
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  const pad = (n) => String(n).padStart(2, '0');
  if (hrs > 0) {
    return `${hrs}h ${pad(mins)}m ${pad(secs)}s`;
  }
  return `${pad(mins)}m ${pad(secs)}s`;
}

/**
 * Evaluates schedule state:
 * - 'IDLE'
 * - 'SCHEDULED_COUNTDOWN' (timer ticking before maintenance starts)
 * - 'ACTIVE' (maintenance is in progress: full blur page)
 */
export function evaluateMaintenanceStatus(schedule) {
  if (!schedule || !schedule.is_enabled) {
    return { status: 'IDLE', secondsRemaining: 0, countdownText: '' };
  }

  // If mode is scheduled and scheduled_at is in the future
  if (schedule.mode === 'scheduled' && schedule.scheduled_at) {
    const scheduledTime = new Date(schedule.scheduled_at).getTime();
    const now = Date.now();
    const diffSeconds = Math.floor((scheduledTime - now) / 1000);

    if (diffSeconds > 0) {
      return {
        status: 'SCHEDULED_COUNTDOWN',
        secondsRemaining: diffSeconds,
        countdownText: formatCountdownTime(diffSeconds),
      };
    }
  }

  // If mode is instant OR scheduled time has been reached
  return {
    status: 'ACTIVE',
    secondsRemaining: 0,
    countdownText: '',
  };
}

export function useMaintenanceSchedule({ pollIntervalMs = 10000 } = {}) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({
    status: 'IDLE',
    secondsRemaining: 0,
    countdownText: '',
  });

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await getMaintenanceSchedule();
      if (res && res.data) {
        setSchedule(res.data);
      }
    } catch (err) {
      console.warn('Could not fetch maintenance schedule:', err?.message || err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll backend schedule periodically
  useEffect(() => {
    fetchSchedule();
    const pollId = setInterval(fetchSchedule, pollIntervalMs);
    return () => clearInterval(pollId);
  }, [fetchSchedule, pollIntervalMs]);

  // Tick timer every 1 second when countdown is active
  useEffect(() => {
    const updateCountdown = () => {
      const evalResult = evaluateMaintenanceStatus(schedule);
      setState(evalResult);
    };

    updateCountdown();
    const timerId = setInterval(updateCountdown, 1000);
    return () => clearInterval(timerId);
  }, [schedule]);

  return {
    schedule,
    loading,
    status: state.status,
    isMaintenanceActive: state.status === 'ACTIVE',
    isScheduledCountdown: state.status === 'SCHEDULED_COUNTDOWN',
    secondsRemaining: state.secondsRemaining,
    countdownText: state.countdownText,
    refetch: fetchSchedule,
  };
}

export default useMaintenanceSchedule;
